import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface AgentStep {
  tool: string;
  input: string;
  output: string;
  executedAt: string;
}

interface ChatResponseData {
  conversationId: string;
  sessionId: string;
  response: string;
  tokenCount: number;
  timestamp: string;
  agentSteps: AgentStep[] | null;
}

interface Message {
  role: 'user' | 'bot';
  content: string;
  agentSteps?: AgentStep[];
}

interface ServerLogEntry {
  timestamp: string;
  type: 'USER' | 'TOOL_CALL' | 'TOOL_RESULT' | 'AGENT_RESPONSE' | 'ERROR';
  content: string;
}

interface LogEntry {
  timestamp: string;
  type: 'user' | 'tool_call' | 'tool_result' | 'response' | 'error';
  content: string;
}

interface ToolExecution {
  id: number;
  timestamp: string;
  toolName: string;
  args: string;
  result: string;
  success: boolean;
}

interface DemoChatProps {
  widgetId: string;
  apiKey: string;
  agentUrl: string;
  suggestedMessages?: string[];
}

export default function DemoChat({ widgetId, apiKey, agentUrl, suggestedMessages }: DemoChatProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [bottomTab, setBottomTab] = useState<'console' | 'executions'>('executions');
  const [bottomCollapsed, setBottomCollapsed] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const logIndexRef = useRef(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (bottomTab === 'console') {
      const el = logsContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [logs, bottomTab]);

  const serverTypeMap: Record<string, LogEntry['type']> = {
    USER: 'user',
    TOOL_CALL: 'tool_call',
    TOOL_RESULT: 'tool_result',
    AGENT_RESPONSE: 'response',
    ERROR: 'error',
  };

  // Build executions list from logs (pair tool_call + tool_result)
  const executions = useMemo<ToolExecution[]>(() => {
    const result: ToolExecution[] = [];
    let counter = 0;
    for (let i = 0; i < logs.length; i++) {
      if (logs[i].type === 'tool_call') {
        const callContent = logs[i].content;
        const parenIdx = callContent.indexOf('(');
        const toolName = parenIdx > 0 ? callContent.substring(0, parenIdx) : callContent;
        const args = parenIdx > 0 ? callContent.substring(parenIdx + 1, callContent.length - 1) : '';

        // Look for matching result
        let resultContent = '';
        let success = true;
        if (i + 1 < logs.length && logs[i + 1].type === 'tool_result') {
          resultContent = logs[i + 1].content;
          // Check if result indicates an error
          try {
            const parsed = JSON.parse(resultContent);
            if (parsed.error || parsed.success === false) success = false;
          } catch {
            if (resultContent.includes('failed') || resultContent.includes('error') || resultContent.includes('Operation failed')) {
              success = false;
            }
          }
        }

        result.push({
          id: counter++,
          timestamp: logs[i].timestamp,
          toolName,
          args,
          result: resultContent,
          success,
        });
      }
    }
    return result;
  }, [logs]);

  const startPolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${agentUrl}/api/v1/demo/logs/${sessionId}?from=${logIndexRef.current}`);
        if (!res.ok) return;
        const data = await res.json();
        const entries: ServerLogEntry[] = data.entries;
        if (entries.length > 0) {
          const newLogs = entries.map(e => ({
            timestamp: new Date(e.timestamp).toLocaleTimeString('ka-GE', { hour12: false }),
            type: serverTypeMap[e.type] || 'error' as LogEntry['type'],
            content: e.content,
          }));
          setLogs(prev => [...prev, ...newLogs]);
          logIndexRef.current = data.total;
        }
      } catch {
        // ignore polling errors
      }
    }, 500);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopPolling();
  }, []);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMessage: Message = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    startPolling();

    try {
      const res = await fetch(`${agentUrl}/api/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          sessionId,
          widgetId,
          message: text.trim(),
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data: ChatResponseData = await res.json();
      setMessages(prev => [...prev, {
        role: 'bot',
        content: data.response,
        agentSteps: data.agentSteps || undefined,
      }]);
    } catch (err) {
      const errorText = err instanceof Error ? err.message : 'Unknown error';
      setMessages(prev => [...prev, { role: 'bot', content: `Error: ${errorText}` }]);
    } finally {
      stopPolling();
      try {
        const res = await fetch(`${agentUrl}/api/v1/demo/logs/${sessionId}?from=${logIndexRef.current}`);
        if (res.ok) {
          const data = await res.json();
          const entries: ServerLogEntry[] = data.entries;
          if (entries.length > 0) {
            const newLogs = entries.map(e => ({
              timestamp: new Date(e.timestamp).toLocaleTimeString('ka-GE', { hour12: false }),
              type: serverTypeMap[e.type] || 'error' as LogEntry['type'],
              content: e.content,
            }));
            setLogs(prev => [...prev, ...newLogs]);
            logIndexRef.current = data.total;
          }
        }
      } catch {
        // ignore
      }
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const logColors: Record<LogEntry['type'], string> = {
    user: 'text-cyan-400',
    tool_call: 'text-yellow-400',
    tool_result: 'text-green-400',
    response: 'text-blue-300',
    error: 'text-red-400',
  };

  const logPrefixes: Record<LogEntry['type'], string> = {
    user: 'USER',
    tool_call: 'TOOL',
    tool_result: 'RESULT',
    response: 'AGENT',
    error: 'ERROR',
  };

  const executionCount = executions.length;

  return (
    <div className="flex flex-col h-full">
      {/* Chat area */}
      <div className="flex flex-col flex-1 min-h-0 bg-gray-50 rounded-t-lg border border-b-0">
        {/* Suggested messages - sticky top */}
        {suggestedMessages && suggestedMessages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 p-2 border-b border-gray-200 bg-white rounded-t-lg flex-shrink-0">
            {suggestedMessages.map((msg, i) => (
              <button
                key={i}
                onClick={() => sendMessage(msg)}
                disabled={loading}
                className="text-left px-2.5 py-1 text-xs bg-gray-50 border border-gray-200 rounded-md hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-gray-300 font-mono mr-1">{i + 1}.</span>
                {msg}
              </button>
            ))}
          </div>
        )}

        {/* Messages - scrollable */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-400">{t('examples.tryAsking')}</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i}>
              <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-line ${
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border text-gray-800'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border rounded-lg px-4 py-2 text-sm text-gray-500">
                <span className="inline-flex gap-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </div>
            </div>
          )}

        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="border-t p-3 flex gap-2 flex-shrink-0">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={t('examples.inputPlaceholder')}
            disabled={loading}
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('examples.sendMessage')}
          </button>
        </form>
      </div>

      {/* Bottom Panel - Console & Executions tabs */}
      <div className={`bg-gray-900 rounded-b-lg border border-gray-700 flex flex-col ${bottomCollapsed ? 'flex-shrink-0' : 'flex-1 min-h-0'}`}>
        {/* Tab header */}
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setBottomCollapsed(!bottomCollapsed)}
              className="text-gray-400 hover:text-gray-200 px-1 text-xs"
            >
              {bottomCollapsed ? '\u25B6' : '\u25BC'}
            </button>
            <button
              onClick={() => { setBottomTab('executions'); setBottomCollapsed(false); }}
              className={`px-2 py-0.5 text-xs font-mono rounded ${
                bottomTab === 'executions' ? 'bg-gray-700 text-gray-200' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              Executions {executionCount > 0 && <span className="text-gray-400">({executionCount})</span>}
            </button>
            <button
              onClick={() => { setBottomTab('console'); setBottomCollapsed(false); }}
              className={`px-2 py-0.5 text-xs font-mono rounded ${
                bottomTab === 'console' ? 'bg-gray-700 text-gray-200' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t('examples.console')} {logs.length > 0 && <span className="text-gray-400">({logs.length})</span>}
            </button>
          </div>
          {logs.length > 0 && (
            <button
              onClick={() => {
                setLogs([]);
                logIndexRef.current = 0;
                fetch(`${agentUrl}/api/v1/demo/logs/${sessionId}`, { method: 'DELETE' }).catch(() => {});
              }}
              className="text-gray-500 hover:text-gray-300 text-xs font-mono px-1"
            >
              clear
            </button>
          )}
        </div>

        {/* Tab content */}
        {!bottomCollapsed && (
          <div className="flex-1 overflow-y-auto min-h-0">
            {bottomTab === 'executions' ? (
              <div className="p-2 space-y-1">
                {executions.length === 0 ? (
                  <div className="text-gray-600 text-center py-6 text-xs font-mono">
                    Tool execution-ები აქ გამოჩნდება
                  </div>
                ) : (
                  executions.map(exec => (
                    <ExecutionItem key={exec.id} execution={exec} />
                  ))
                )}
              </div>
            ) : (
              <div ref={logsContainerRef} className="p-2 font-mono text-xs leading-relaxed">
                {logs.length === 0 ? (
                  <div className="text-gray-600 text-center py-6">
                    {t('examples.consoleEmpty')}
                  </div>
                ) : (
                  logs.map((log, i) => (
                    <div key={i} className="flex gap-2 py-0.5">
                      <span className="text-gray-600 flex-shrink-0">{log.timestamp}</span>
                      <span className={`flex-shrink-0 ${logColors[log.type]}`}>[{logPrefixes[log.type]}]</span>
                      <span className="text-gray-300 whitespace-pre-wrap break-all">{log.content}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ExecutionItem({ execution }: { execution: ToolExecution }) {
  const [expanded, setExpanded] = useState(false);

  let formattedArgs: string;
  try {
    formattedArgs = JSON.stringify(JSON.parse(execution.args), null, 2);
  } catch {
    formattedArgs = execution.args;
  }

  let formattedResult: string;
  try {
    formattedResult = JSON.stringify(JSON.parse(execution.result), null, 2);
  } catch {
    formattedResult = execution.result;
  }

  return (
    <div className="rounded border border-gray-700 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-mono hover:bg-gray-800 transition-colors"
      >
        <span className="text-gray-500">{expanded ? '\u25BC' : '\u25B6'}</span>
        <span className={execution.success ? 'text-green-400' : 'text-red-400'}>
          {execution.success ? '\u2713' : '\u2717'}
        </span>
        <span className="text-yellow-300 font-semibold">{execution.toolName}</span>
        <span className="text-gray-600 ml-auto">{execution.timestamp}</span>
      </button>
      {expanded && (
        <div className="border-t border-gray-700 p-2 space-y-2 text-xs font-mono">
          <div>
            <span className="text-gray-500 text-[10px] uppercase">Input</span>
            <pre className="mt-0.5 bg-gray-950 rounded p-2 text-cyan-300 overflow-x-auto whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
              {formattedArgs || '(none)'}
            </pre>
          </div>
          <div>
            <span className="text-gray-500 text-[10px] uppercase">Output</span>
            <pre className={`mt-0.5 bg-gray-950 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-48 overflow-y-auto ${
              execution.success ? 'text-green-300' : 'text-red-300'
            }`}>
              {formattedResult || '(empty)'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
